import * as twgl from 'twgl.js';
import debounce from '../utils/debounce';
import gsap from 'gsap';

// fragment shader
const frag = `
    precision mediump float;

    uniform sampler2D texA;
    uniform sampler2D texB;    
    uniform sampler2D noise;
    uniform vec2 resolution;
    uniform float fade;
    uniform float displacement;    
    uniform float time;
    uniform vec2 mouse;
    uniform float mouseStrength;
    uniform vec2 fadeCenter;

    #define PI 3.141593625
    #define DISP_SCALE .15


    void main() {
        vec2 uv = gl_FragCoord.xy / resolution.xy;
        uv.y = 1. - uv.y;

        // normalized uv with 0,0 at the center
        vec2 cuv = (uv * 2.) - 1.;

        // calculate ring fade
        float f = min(1., max(0., (fade - (.4 * distance(fadeCenter, cuv))) * 5.));

        // displacement map based on mouse position
        float dispCalc = (1. - (distance(mouse, cuv) + .4));
        dispCalc += distance(f, 0.) * displacement;
        dispCalc = min(1., max(0., dispCalc));
        dispCalc = pow(dispCalc, 2.);
        dispCalc *= mouseStrength;

        // read noise texture
        vec4 nt = texture2D(noise, uv);

        // texture/dispCalc A
        float n = fract(2.*nt.r+time);
        float disp = (n-.5)*DISP_SCALE*dispCalc;
        vec4 ta = texture2D(texA, uv + disp);

        // texture/dispCalc B
        vec4 tb = texture2D(texB, uv + disp);
        
        // mix texA and B based on fade uniform
        vec4 c = mix(ta, tb, f);

        // return the final color
        gl_FragColor = c;
    }
`;

// boilerplate vertex shader
const vert = `
    attribute vec4 position;

    void main() {
        gl_Position = position;
    }
`;

class GLTopper {
  constructor(el) {
    const windowWidth = window.innerWidth;
    const imageSizes = [768, 1024, 1440, 1660, 2400];
    const currentSize = imageSizes.sort(
      (a, b) => Math.abs(windowWidth - a) - Math.abs(windowWidth - b),
    )[0];

    // list of images to show in topper
    this.images = [
      {
        theme: 'light',
        asset: {
          name: 'topper-asset-0',
          alt: 'A surreal earth-like disc reminiscent of a globe.',
          path: `/static/img/topper-asset-0-${currentSize}.jpg`,
        },
      },
      {
        theme: 'blue',
        asset: {
          name: 'topper-asset-1',
          alt: 'A surreal earth-like disc reminiscent of a globe.',
          path: `/static/img/topper-asset-1-${currentSize}.jpg`,
        },
      },
      {
        theme: 'yellow',
        asset: {
          name: 'topper-asset-2',
          alt: 'A surreal earth-like disc reminiscent of a globe.',
          path: `/static/img/topper-asset-2-${currentSize}.jpg`,
        },
      },
      {
        theme: 'dark',
        asset: {
          name: 'topper-asset-3',
          alt: 'A surreal earth-like disc reminiscent of a globe.',
          path: `/static/img/topper-asset-3-${currentSize}.jpg`,
        },
      },
      {
        theme: 'red',
        asset: {
          name: 'topper-asset-4',
          alt: 'A surreal earth-like disc reminiscent of a globe.',
          path: `/static/img/topper-asset-4-${currentSize}.jpg`,
        },
      },
      {
        theme: 'green',
        asset: {
          name: 'topper-asset-5',
          alt: 'A surreal earth-like disc reminiscent of a globe.',
          path: `/static/img/topper-asset-5-${currentSize}.jpg`,
        },
      },
      {
        theme: 'tennis',
        asset: {
          name: 'topper-asset-6',
          alt: 'A surreal earth-like disc reminiscent of a globe.',
          path: `/static/img/topper-asset-6-${currentSize}.jpg`,
        },
      },
    ];

    // check for webgl support using vanilla js
    let gl;
    const canvas = document.createElement('canvas');
    try {
      gl = canvas.getContext('webgl');
    } catch (x) {
      gl = null;
    }

    if (gl === null) {
      try {
        gl = canvas.getContext('experimental-webgl');
      } catch (x) {
        gl = null;
      }
    }

    // if no webgl support, or prefers reduced motion,
    // show first image as a fallback
    if (gl === null || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const fallback = document.createElement('img');
      fallback.src = this.images[0].asset.path;
      fallback.alt = this.images[0].asset.alt;
      fallback.classList.add('gl-topper__fallback');
      el.appendChild(fallback);
      return;
    }

    // initialize callbacks and uniforms
    this.container = el;
    this.initGL = this.initGL.bind(this);
    this.loop = this.loop.bind(this);
    this.count = this.images.length;
    this.mouse = [0, 0];
    this.fadeCenter = [0, 0];
    this.transitioning = false;
    this.currentIdx = 0;

    // initialize and launch GL scene
    this.initGL(el);

    // initialize timelines for image transition
    this.tl = gsap.timeline({ paused: true, repeat: 0 });
    this.tl.timeScale(0.5);

    // image A -> B wipe
    this.tl.to(this.uniforms, {
      fade: 1,
      duration: 1,
    });

    // blur effect in
    this.tl.to(
      this.uniforms,
      {
        displacement: 1,
        duration: 0.1,
      },
      '<',
    );

    // blur effect out, and prep for next transition
    this.tl.to(
      this.uniforms,
      {
        displacement: 0,
        duration: 1,
        onComplete: () => {
          // increment current image and wrap around if neccessary
          if (++this.currentIdx >= this.images.length) {
            this.currentIdx = 0;
          }
          const nextIdx = this.currentIdx + 1 >= this.images.length ? 0 : this.currentIdx + 1;

          // swap texture B -> A, load next texture as B
          this.uniforms.texA = this.textures[this.currentIdx];
          this.uniforms.texB = this.textures[nextIdx];

          // fade back to A and reset timeline
          this.uniforms.fade = 0;
          this.tl.seek(0);
          this.tl.pause();

          // clear transitioning flag to handle clicks again
          this.transitioning = false;
        },
      },
      '>',
    );

    // setup mouse event handlers
    this.handleMouseOver = debounce(this.handleMouseOver.bind(this, el), 2);
    this.container.addEventListener('mousemove', this.handleMouseOver);

    this.handleClick = this.handleClick.bind(this);
    this.container.addEventListener('click', this.handleClick);

    // fade blur effect in when hovering
    this.container.addEventListener('mouseover', () => {
      gsap.to(this.uniforms, { mouseStrength: 1, duration: 0.5 });
    });

    // fade blur effect out when not
    this.container.addEventListener('mouseout', () => {
      gsap.to(this.uniforms, { mouseStrength: 0, duration: 0.5 });
    });
  }

  // frameloop
  loop(time) {
    // alias a few vars we use a lot
    const gl = this.gl;
    const bi = this.bufferInfo;
    const pi = this.programInfo;

    // just resize every frame - TODO is this internally optimized to avoid dupes?
    twgl.resizeCanvasToDisplaySize(this.canvas, window.devicePixelRatio);
    gl.viewport(0, 0, gl.canvas.width, gl.canvas.height);

    // update uniforms
    this.uniforms.time = time * 0.0005;
    this.uniforms.resolution = [this.gl.canvas.width, this.gl.canvas.height];
    this.uniforms.mouse = this.mouse;
    this.uniforms.fadeCenter = this.fadeCenter;

    // setup scene and render
    gl.useProgram(pi.program);
    twgl.setBuffersAndAttributes(gl, pi, bi);
    twgl.setUniforms(pi, this.uniforms);
    twgl.drawBufferInfo(gl, bi);

    // rinse and repeat
    window.requestAnimationFrame(this.loop);
  }

  handleMouseOver(target, evt) {
    // calculate a 0. - 1. value for mouse pos
    this.mouse[0] = evt.offsetX / target.clientWidth;
    this.mouse[1] = evt.offsetY / target.clientHeight;

    // remap it to be -1. - 1. in both dimensions
    this.mouse = this.mouse.map(v => (v - 0.5) * 2);
  }

  handleClick(evt) {
    // skip if already transitioning
    if (this.transitioning) {
      return;
    }

    // set transition flag and fade center location (-1. to 1.)
    this.transitioning = true;
    this.fadeCenter[0] = evt.offsetX / this.container.clientWidth;
    this.fadeCenter[1] = evt.offsetY / this.container.clientHeight;
    this.fadeCenter = this.fadeCenter.map(v => (v - 0.5) * 2);

    // play the timeline
    this.tl.play();
  }

  initGL(el) {
    // set background color
    twgl.setDefaults({ textureColor: [0, 0, 0, 1] });

    // initialize canvas
    this.canvas = document.createElement('canvas');
    this.gl = this.canvas.getContext('webgl', {
      premultipliedAlpha: false,
    });

    // compile shaders and send to GPU
    this.programInfo = twgl.createProgramInfo(this.gl, [vert, frag]);

    // initialize a square surface that covers the entire canvas we can render to
    const arrays = {
      position: [-1, -1, 0, 1, -1, 0, -1, 1, 0, -1, 1, 0, 1, -1, 0, 1, 1, 0],
    };
    this.bufferInfo = twgl.createBufferInfoFromArrays(this.gl, arrays);

    // load the noise texture
    this.noiseTex = twgl.createTexture(this.gl, {
      src: `/static/img/disp_painting_swirl.jpg`,
      crossOrigin: '',
    });

    // preload all textures
    const texList = this.images.map(theme => ({ src: theme.asset.path }));
    this.textures = twgl.createTextures(this.gl, texList);

    // load the first texture from the theme listing
    this.texA = this.textures[0];

    // load the first texture from the theme listing
    this.texB = this.textures[1];

    // initialize uniforms
    this.uniforms = {
      time: 0,
      resolution: [this.gl.canvas.width, this.gl.canvas.height],
      texA: this.texA,
      texB: this.texB,
      noise: this.noiseTex,
      fade: 0.0,
      displacement: 0.0,
      mouse: this.mouse,
      mouseStrength: 0,
      fadeCenter: this.fadeCenter,
    };

    // add canvas to the dom
    el.appendChild(this.canvas);

    // begin rendering on next frame start
    window.requestAnimationFrame(this.loop);
  }
}

export default GLTopper;
